import styled, { DefaultTheme } from 'styled-components';
import { Button, Card, Input, Pagination, Tag } from 'antd';

interface StretchCardProps {
	$isSelected: boolean;
	$isDisabled: boolean;
	$isOutOfRange: boolean;
}

type StretchCardStyle = StretchCardProps & { theme: DefaultTheme };

function getCardBackground({ theme, $isSelected, $isOutOfRange }: StretchCardStyle): string {
	if ($isOutOfRange) {
		return theme.palette.secondary[50];
	}

	if ($isSelected) {
		return theme.palette.primary[50];
	}

	return theme.palette.secondary.white;
}

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
`;

export const Title = styled.h3`
	flex: 1;
	margin: 0;
	font-size: 1.5rem;
	line-height: 2rem;
`;

export const SearchInput = styled(Input)`
	height: 2.5rem;
	max-width: 28.5rem;
	border-radius: 0.25rem;
`;

export const FilterButton = styled(Button)<{ $isActive: boolean }>`
	height: 2.5rem;
	padding: 0 0.8125rem;
	white-space: nowrap;
	border-radius: 0.25rem;
	background: ${({ theme, $isActive }) => ($isActive ? theme.palette.primary[100] : theme.palette.secondary.white)};
	border-color: ${({ theme, $isActive }) => ($isActive ? theme.palette.primary[100] : theme.palette.secondary[150])};
	color: ${({ theme, $isActive }) => ($isActive ? theme.palette.primary.default : theme.palette.secondary[300])};

	&:hover,
	&:focus {
		background: ${({ theme, $isActive }) =>
			$isActive ? theme.palette.primary[100] : theme.palette.secondary.white};
		border-color: ${({ theme }) => theme.palette.primary[100]};
		color: ${({ theme }) => theme.palette.primary.default};
	}
`;

export const StretchPagination = styled(Pagination)`
	.ant-pagination-item {
		background: ${({ theme }) => theme.palette.secondary[100]};
		border: none;
		border-radius: 0.25rem;

		a {
			color: ${({ theme }) => theme.palette.secondary[250]};
		}
	}

	.ant-pagination-item-active {
		background: ${({ theme }) => theme.palette.primary[100]};

		a {
			color: ${({ theme }) => theme.palette.primary.default};
			font-weight: 600;
		}
	}

	.ant-pagination-item-link {
		background: transparent;
		border: none;
	}
`;

export const CardsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(20.6875rem, 1fr));
	gap: 1rem;
`;

export const StretchCard = styled(Card)<StretchCardProps>`
	cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
	opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};
	overflow: hidden;
	border-radius: 1rem;
	border-width: ${({ $isSelected, $isOutOfRange }) => ($isSelected || $isOutOfRange ? '2px' : '1px')};
	border-style: ${({ $isOutOfRange }) => ($isOutOfRange ? 'dashed' : 'solid')};
	border-color: ${({ theme, $isSelected }) =>
		$isSelected ? theme.palette.primary[600] : theme.palette.secondary[150]};
	background: ${getCardBackground};

	.ant-card-body {
		display: flex;
		align-items: center;
		gap: 1rem;
		padding: 1rem;

		&::before,
		&::after {
			display: none;
		}
	}
`;

export const CardInfo = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 0;
`;

export const CardTitle = styled.span`
	overflow: hidden;
	color: ${({ theme }) => theme.palette.secondary[400]};
	font-size: 1rem;
	font-weight: 600;
	line-height: 1.5rem;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const CardStretchCount = styled.span`
	overflow: hidden;
	color: ${({ theme }) => theme.palette.secondary[300]};
	font-size: 0.875rem;
	line-height: 1rem;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const StatusTag = styled(Tag)`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	gap: 0.25rem;
	margin: 0;
	padding: 0.5rem;
	background: ${({ theme }) => theme.palette.secondary[100]};
	border: none;
	border-radius: 0.25rem;
	color: ${({ theme }) => theme.palette.secondary[300]};
	font-size: 0.875rem;
	font-weight: 600;
	line-height: 1rem;

	.anticon {
		font-size: 1rem;
	}
`;

export const FooterRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: ${({ theme }) => theme.palette.secondary[300]};
	font-size: 0.875rem;
`;

export const EmptyMessage = styled.p`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 5.5rem;
	max-width: 25rem;
	margin: 1rem auto 0;
	color: ${({ theme }) => theme.palette.secondary[300]};
	font-size: 1rem;
	line-height: 1.5rem;
	text-align: center;
`;

export const RpnTag = styled(Tag)`
	flex-shrink: 0;
	margin: 0;
	padding: 0.5rem;
	background: #f5f6ff;
	border: 1px solid ${({ theme }) => theme.palette.primary[600]};
	border-radius: 0.25rem;
	color: ${({ theme }) => theme.palette.primary[600]};
	font-size: 0.875rem;
	font-weight: 600;
	line-height: 1rem;
	text-align: center;
`;

export const ReviewStretchButton = styled(Button)`
	flex-shrink: 0;
	height: auto;
	padding: 0;
	color: ${({ theme }) => theme.palette.primary.default};
	font-size: 0.875rem;
	line-height: 1rem;
	text-decoration: underline;

	&:hover,
	&:focus {
		color: ${({ theme }) => theme.palette.primary.default};
		text-decoration: underline;
	}
`;
